/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
export type SwipeIndexDelta = -1 | 0 | 1;

interface SwipePoint {
    x: number;
    y: number;
}

const MIN_SWIPE_DISTANCE_PX = 48;
const WIDTH_THRESHOLD_RATIO = 0.12;
const HORIZONTAL_INTENT_RATIO = 1.25;

/**
 * Resolve a completed gesture to one discrete gallery step.
 *
 * Positive means the next image, negative means the previous image, and zero
 * means the gesture should be left to normal page interaction.
 */
export function getSwipeIndexDelta(start: SwipePoint, end: SwipePoint, surfaceWidth: number): SwipeIndexDelta {
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const horizontalDistance = Math.abs(deltaX);
    const verticalDistance = Math.abs(deltaY);
    const threshold = Math.max(MIN_SWIPE_DISTANCE_PX, surfaceWidth * WIDTH_THRESHOLD_RATIO);

    if (horizontalDistance < threshold || horizontalDistance <= verticalDistance * HORIZONTAL_INTENT_RATIO) {
        return 0;
    }

    return deltaX < 0 ? 1 : -1;
}

export function getWrappedGalleryIndex(currentIndex: number, indexDelta: Exclude<SwipeIndexDelta, 0>, count: number) {
    if (count <= 0) return 0;
    return (currentIndex + indexDelta + count) % count;
}
